// src/pages/AdminDealerships.jsx
// STATUS: active | UPDATED: 2026-08-29
// LOG:
//   2026-08-29  New. Look a dealership up, then set its brand, then invite it.
//
// A store in Arkansas is not the same business as one in Oregon, and the product
// should not open looking identical for both.
//
// THE ORDER MATTERS
//
// Research first, store second, invites third. That is the actual workflow: look
// them up, read what came back, decide, then bring them on. So the lookup box
// sits above the list and works with no store selected, and
// `dealership_research.dealership_id` is nullable so a finding can exist before
// the dealership does.
//
// THE ACCENT IS STRUCTURAL ONLY
//
// Eyebrows, rules, active marks. The same job oxblood does in the house theme. It
// is never a background, because a dealership's red behind white text is somebody
// else's accessibility problem and we are not inheriting it. The swatches offered
// here come from colours actually found on the store's own site, ranked by how
// often they appear, and a hex can always be typed instead.
//
// NOTHING IS GUESSED
//
// If a site cannot be read, the panel says so and the fields stay empty. An
// invented brand colour is fake data with a hex code on it, and it would be wrong
// on the one screen a new dealership sees first.

import { useCallback, useEffect, useState } from 'react'
import {
  Box, Flex, HStack, VStack, Text, Icon, Button, Input,
  Spinner, Alert, AlertIcon, SimpleGrid, Badge, useToast, Divider, Link, Select,
} from '@chakra-ui/react'
import { Building2, Search, Check, Upload, Globe, Palette, Gauge, MapPin } from 'lucide-react'
import { Link as RouterLink } from 'react-router-dom'
import { supabase } from '../lib/supabase'

const BUCKET = 'dealership-logos'

function slugify(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60)
}

function logoUrl(path) {
  if (!path) return null
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path)
  return data?.publicUrl || null
}

function Swatch({ hex, selected, onPick }) {
  return (
    <Box
      as="button"
      type="button"
      onClick={() => onPick(hex)}
      w="100%"
      borderRadius="md"
      overflow="hidden"
      border="2px solid"
      borderColor={selected ? 'ink' : 'line'}
      transition="border-color 0.12s ease"
      _hover={{ borderColor: 'ink' }}
      textAlign="left"
    >
      <Box h="34px" bg={hex} />
      <Text
        fontSize="10px" fontFamily="mono" color="inkMuted"
        px={2} py={1} letterSpacing="0.04em"
      >
        {hex}
      </Text>
    </Box>
  )
}

// ---------------------------------------------------------------- research box

function Lookup({ onCreated }) {
  const toast = useToast()
  const [url, setUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  async function run() {
    const target = url.trim()
    if (!target) return
    setBusy(true)
    setError(null)
    setResult(null)

    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      setBusy(false)
      setError('Your session expired. Sign in again.')
      return
    }

    try {
      const res = await fetch('/.netlify/functions/research-dealership', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ url: target.startsWith('http') ? target : `https://${target}` }),
      })
      const body = await res.json()
      if (!res.ok) {
        setError(body.error || 'That did not work.')
      } else {
        setResult(body)
      }
    } catch {
      setError('Could not reach the research function.')
    }
    setBusy(false)
  }

  // Turning a page title into a business name.
  //
  // The first version split on | and - and took the head, which produced
  // "John's Honda: ATVs, UTVs, Motorcycles & Scooters in Hot Springs AR" as a
  // dealership name, because that title separates with a colon. Splitting on
  // one character was never going to hold across four vendors.
  //
  // Cut at the first real separator of any kind, drop a trailing location
  // clause, and cap it. A human can always correct the field afterward, which
  // is the point of showing it rather than creating silently.
  function businessNameFrom(f) {
    const raw = (f.site_name || f.title || '').trim()
    if (!raw) return ''
    let name = raw.split(/\s[|·–—]\s|:\s|\s-\s/)[0].trim()
    // "in" and "near" and "serving" introduce a location clause. "of" does not:
    // Mahindra of Oklahoma and Crain Hyundai of Fort Smith are the business
    // name, and stripping it produced "Mahindra".
    name = name.replace(/\s+(in|near|serving)\s+[A-Z][\w\s,.']*$/i, '').trim()
    return name.replace(/[,\s]+$/, '').slice(0, 60)
  }

  async function createStore() {
    const f = result?.findings || {}
    const name = businessNameFrom(f)
    if (!name) {
      toast({ status: 'warning', title: 'No name found. Add the store by hand below.' })
      return
    }
    const { data, error: err } = await supabase
      .from('dealerships')
      .insert({
        name,
        slug: slugify(name),
        website: result.source_url,
        phone: f.phone || null,
        makes: f.makes?.length ? f.makes : null,
        accent_hex: f.colors?.[0]?.hex || null,
      })
      .select()
      .single()

    if (err || !data) {
      toast({ status: 'error', title: 'Could not create the store', description: err?.message })
      return
    }
    // Attach the research to the store it produced.
    await supabase.from('dealership_research').update({ dealership_id: data.id }).eq('id', result.id)
    toast({ status: 'success', title: `${name} created. Set the brand below.` })
    setResult(null)
    setUrl('')
    onCreated()
  }

  const f = result?.findings || {}

  return (
    <Box bg="card" border="1px solid" borderColor="line" borderRadius="card" p={{ base: 5, md: 6 }}>
      <HStack spacing={2} mb={2}>
        <Icon as={Search} boxSize={4} color="accent.500" />
        <Text textStyle="eyebrow" color="accent.500">
          Look a dealership up
        </Text>
      </HStack>
      <Text fontSize="body-sm" color="inkMuted" lineHeight={1.6} maxW="measure" mb={4}>
        Reads their public homepage and reports what is actually there. Nothing is
        guessed. Run this before inviting anybody, so the store opens knowing where
        it is.
      </Text>

      <Flex gap={3} wrap="wrap">
        <Input
          flex="1 1 280px"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && run()}
          placeholder="example-motors.com"
          bg="surface"
          borderColor="line"
          fontSize="body-sm"
          _focusVisible={{ borderColor: 'accent.500', boxShadow: 'none' }}
        />
        <Button bg="ink" color="onInk" _hover={{ opacity: 0.88 }} onClick={run} isLoading={busy} loadingText="Reading">
          Run research
        </Button>
      </Flex>

      {error && (
        <Alert status="error" mt={4} borderRadius="md" fontSize="sm">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {result && (
        <Box mt={5} pt={5} borderTop="1px solid" borderColor="line">
          <HStack spacing={2} mb={3}>
            <Badge
              bg={result.confidence === 'high' ? 'surface' : 'surface'}
              color={result.confidence === 'none' ? 'inkMuted' : 'ink'}
              fontSize="10px" textTransform="uppercase" letterSpacing="0.08em"
              px={2} py={0.5} borderRadius="full" fontWeight={600}
            >
              {result.confidence} confidence
            </Badge>
            {f.makes?.length > 0 && (
              <Text fontSize="12px" color="inkMuted">
                {f.makes.join(', ')}
              </Text>
            )}
          </HStack>

          {result.summary && (
            <Text fontSize="body-sm" color="ink" lineHeight={1.65} maxW="measure" mb={4}>
              {result.summary}
            </Text>
          )}

          {f.colors?.length > 0 && (
            <>
              <Text textStyle="eyebrow" color="inkMuted" mb={2}>
                Colours found on their site
              </Text>
              <SimpleGrid columns={{ base: 3, md: 6 }} spacing={2} mb={4}>
                {f.colors.map((c) => (
                  <Swatch key={c.hex} hex={c.hex} selected={false} onPick={() => {}} />
                ))}
              </SimpleGrid>
            </>
          )}

          {f.og_image && (
            <Box mb={4}>
              <Text textStyle="eyebrow" color="inkMuted" mb={2}>
                Logo candidate
              </Text>
              <Box
                as="img"
                src={f.og_image}
                alt=""
                maxH="60px"
                maxW="240px"
                objectFit="contain"
                bg="surface"
                borderRadius="md"
                p={2}
              />
            </Box>
          )}

          {result.confidence !== 'none' && (
            <Button size="sm" bg="ink" color="onInk" _hover={{ opacity: 0.88 }} onClick={createStore}>
              Create this store
            </Button>
          )}
        </Box>
      )}
    </Box>
  )
}

// ------------------------------------------------------------------ store card

// WHAT THIS STORE SELLS, AND WHY IT IS WORTH A FIELD.
//
// `dealerships.industry` has existed since 0010 and on 2026-09-26 every row
// in the table was null, because nothing in this app could set it. Two things
// key on it and neither had ever run: the coach's vocabulary block, which
// stops it reading the generic word "unit" out loud at somebody who has never
// used it, and from today the market conditions for that sector.
//
// So this is not a new feature. It is the switch that turns on two that were
// already built. See CLAUDE.md on checking whether a thing is built and
// unused before calling it missing.
//
// The values match the CHECK in migration 0045. "Not recorded" is a real
// answer and the honest default: the coach handles a null by working around
// the noun and saying nothing about the market, which is better than a guess.
const INDUSTRIES = [
  ['', 'Not recorded'],
  ['powersports', 'Powersports'],
  ['rv', 'RV'],
  ['tractor', 'Tractor and ag equipment'],
  ['heavy', 'Heavy equipment'],
  ['automotive', 'Automotive'],
  ['other', 'Something else'],
]

function StoreCard({ store, onSaved }) {
  const toast = useToast()
  const [form, setForm] = useState({
    name: store.name || '',
    website: store.website || '',
    city: store.city || '',
    state: store.state || '',
    phone: store.phone || '',
    accent_hex: store.accent_hex || '',
    makes: (store.makes || []).join(', '),
    industry: store.industry || '',
  })
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [swatches, setSwatches] = useState([])
  const [locations, setLocations] = useState([])

  useEffect(() => {
    let live = true
    supabase
      .from('dealership_research')
      .select('findings')
      .eq('dealership_id', store.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .then(({ data }) => {
        if (live && data?.[0]?.findings?.colors) setSwatches(data[0].findings.colors)
      })
    loadLocations()
    return () => {
      live = false
    }
  }, [store.id])

  // Locations are the buildings, and a report is about a building's own page.
  // Rocky Ridge's group homepage and their individual location pages score
  // differently, so averaging them would hide both.
  async function loadLocations() {
    const { data, error } = await supabase
      .from('locations')
      .select('*, storefront_reports(overall, share_token, created_at)')
      .eq('dealership_id', store.id)
      .order('is_primary', { ascending: false })
      .order('name')
    if (!error && data) setLocations(data)
  }

  async function addLocation() {
    const name = window.prompt('Location name, for example Idabel or Fort Smith')
    if (!name || !name.trim()) return
    const { error } = await supabase.from('locations').insert({
      dealership_id: store.id,
      name: name.trim(),
      slug: slugify(name),
      is_primary: locations.length === 0,
    })
    if (error) {
      toast({ status: 'error', title: 'Could not add the location', description: error.message })
      return
    }
    loadLocations()
  }

  function set(k, v) {
    setForm((s) => ({ ...s, [k]: v }))
  }

  const hexValid = !form.accent_hex || /^#[0-9A-Fa-f]{6}$/.test(form.accent_hex)

  async function save() {
    if (!hexValid) return
    setSaving(true)
    const { data, error } = await supabase
      .from('dealerships')
      .update({
        name: form.name.trim(),
        website: form.website.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        phone: form.phone.trim() || null,
        accent_hex: form.accent_hex.trim() || null,
        makes: form.makes.trim() ? form.makes.split(',').map((m) => m.trim()).filter(Boolean) : null,
        // Empty string means not recorded, and it has to reach the database as
        // null rather than as '', which the CHECK would reject.
        industry: form.industry || null,
        brand_confirmed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', store.id)
      .select()
      .single()

    setSaving(false)
    if (error || !data) {
      toast({ status: 'error', title: 'Did not save', description: error?.message })
      return
    }
    toast({ status: 'success', title: `${data.name} saved` })
    onSaved(data)
  }

  async function uploadLogo(file) {
    if (!file) return
    if (file.size > 2 * 1024 * 1024) {
      toast({ status: 'warning', title: 'Logo must be under 2MB' })
      return
    }
    setUploading(true)
    const ext = (file.name.split('.').pop() || 'png').toLowerCase()
    const path = `${store.id}/logo.${ext}`

    const { error: upErr } = await supabase.storage
      .from(BUCKET)
      .upload(path, file, { upsert: true, contentType: file.type })

    if (upErr) {
      setUploading(false)
      toast({ status: 'error', title: 'Upload failed', description: upErr.message })
      return
    }

    const { data, error } = await supabase
      .from('dealerships')
      .update({ logo_path: path, updated_at: new Date().toISOString() })
      .eq('id', store.id)
      .select()
      .single()

    setUploading(false)
    if (error || !data) {
      toast({ status: 'error', title: 'Uploaded but not linked', description: error?.message })
      return
    }
    toast({ status: 'success', title: 'Logo set' })
    onSaved(data)
  }

  const currentLogo = logoUrl(store.logo_path)

  return (
    <Box
      bg="card"
      border="1px solid"
      borderColor="line"
      borderLeftWidth="3px"
      borderLeftColor={store.accent_hex || 'line'}
      borderRadius="card"
      p={{ base: 5, md: 6 }}
    >
      <Flex justify="space-between" align="flex-start" gap={4} mb={4} wrap="wrap">
        <Box>
          <Text fontSize="display-sm" fontWeight={600} color="ink" letterSpacing="-0.02em">
            {store.name}
          </Text>
          <HStack spacing={2} mt={1}>
            <Text fontSize="12px" color="inkMuted" fontFamily="mono">
              {store.slug}
            </Text>
            {store.brand_confirmed_at && (
              <Badge
                display="flex" alignItems="center" gap={1}
                bg="surface" color="inkMuted" fontSize="10px"
                textTransform="uppercase" letterSpacing="0.08em"
                px={2} py={0.5} borderRadius="full" fontWeight={600}
              >
                <Check size={10} /> Brand set
              </Badge>
            )}
          </HStack>
        </Box>
        {currentLogo && (
          <Box as="img" src={currentLogo} alt="" maxH="44px" maxW="160px" objectFit="contain" />
        )}
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={4}>
        {[
          ['name', 'Store name'],
          ['website', 'Website'],
          ['city', 'City'],
          ['state', 'State'],
          ['phone', 'Phone'],
          ['makes', 'Makes, comma separated'],
        ].map(([k, label]) => (
          <Box key={k}>
            <Text textStyle="eyebrow" color="inkMuted" mb={1}>
              {label}
            </Text>
            <Input
              value={form[k]}
              onChange={(e) => set(k, e.target.value)}
              bg="surface"
              borderColor="line"
              size="sm"
              fontSize="body-sm"
              _focusVisible={{ borderColor: 'accent.500', boxShadow: 'none' }}
            />
          </Box>
        ))}

        <Box>
          <Text textStyle="eyebrow" color="inkMuted" mb={1}>
            What they sell
          </Text>
          <Select
            value={form.industry}
            onChange={(e) => set('industry', e.target.value)}
            bg="surface"
            borderColor="line"
            size="sm"
            fontSize="body-sm"
            _focusVisible={{ borderColor: 'accent.500', boxShadow: 'none' }}
          >
            {INDUSTRIES.map(([value, label]) => (
              <option key={value || 'none'} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <Text fontSize="12px" color="inkMuted" lineHeight={1.5} mt={1.5} maxW="measure">
            The coach uses this to know what their floor calls the thing they sell, and what their
            market is doing. Left unrecorded it says neither rather than guessing.
          </Text>
        </Box>
      </SimpleGrid>

      <Box mb={4}>
        <HStack spacing={2} mb={2}>
          <Icon as={Palette} boxSize={3.5} color="inkMuted" />
          <Text textStyle="eyebrow" color="inkMuted">
            Accent
          </Text>
        </HStack>
        <Text fontSize="12px" color="inkMuted" lineHeight={1.55} mb={2} maxW="measure">
          Used for eyebrows, rules and active marks only, never as a background.
          Keep it subtle.
        </Text>

        {swatches.length > 0 && (
          <SimpleGrid columns={{ base: 3, md: 6 }} spacing={2} mb={3}>
            {swatches.map((c) => (
              <Swatch
                key={c.hex}
                hex={c.hex}
                selected={form.accent_hex.toUpperCase() === c.hex}
                onPick={(hex) => set('accent_hex', hex)}
              />
            ))}
          </SimpleGrid>
        )}

        <HStack spacing={3}>
          <Input
            value={form.accent_hex}
            onChange={(e) => set('accent_hex', e.target.value)}
            placeholder="#9B2D2D"
            maxW="140px"
            bg="surface"
            borderColor={hexValid ? 'line' : 'red.400'}
            size="sm"
            fontFamily="mono"
            fontSize="body-sm"
            _focusVisible={{ borderColor: 'accent.500', boxShadow: 'none' }}
          />
          {form.accent_hex && hexValid && (
            <Box w="34px" h="34px" borderRadius="md" bg={form.accent_hex} border="1px solid" borderColor="line" />
          )}
          {!hexValid && (
            <Text fontSize="12px" color="red.400">
              Six digit hex, like #9B2D2D
            </Text>
          )}
        </HStack>
      </Box>

      <Divider borderColor="line" mb={4} />

      <Flex gap={3} align="center" wrap="wrap">
        <Button
          as="label"
          size="sm"
          variant="outline"
          borderColor="line"
          color="ink"
          cursor="pointer"
          leftIcon={<Upload size={13} />}
          isLoading={uploading}
        >
          {currentLogo ? 'Replace logo' : 'Upload logo'}
          <input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            hidden
            onChange={(e) => uploadLogo(e.target.files?.[0])}
          />
        </Button>
        <Box flex={1} />
        <Button
          size="sm"
          bg="ink"
          color="onInk"
          _hover={{ opacity: 0.88 }}
          onClick={save}
          isLoading={saving}
          isDisabled={!hexValid}
        >
          Save brand
        </Button>
      </Flex>

      <Box mt={6} pt={5} borderTop="1px solid" borderColor="line">
        <Flex align="center" gap={2} mb={1}>
          <Icon as={MapPin} boxSize={3.5} color="inkMuted" />
          <Text textStyle="eyebrow" color="inkMuted">
            Locations
          </Text>
          <Box flex={1} />
          <Button size="xs" variant="ghost" color="ink" onClick={addLocation}>
            Add a location
          </Button>
        </Flex>
        <Text fontSize="12px" color="inkMuted" mb={3} maxW="measure">
          A report is about one building's own page. Give each location the address
          of its page, not the group homepage.
        </Text>

        <VStack align="stretch" spacing={2}>
          {locations.length === 0 && (
            <Text fontSize="12px" color="inkMuted">
              No locations yet. Every dealership has at least one.
            </Text>
          )}
          {locations.map((loc) => (
            <LocationRow key={loc.id} loc={loc} onChanged={loadLocations} />
          ))}
        </VStack>
      </Box>

    </Box>
  )
}

// ---------------------------------------------------------------- location row

function LocationRow({ loc, onChanged }) {
  const toast = useToast()
  const [site, setSite] = useState(loc.website || '')
  const [running, setRunning] = useState(false)
  const latest = (loc.storefront_reports || [])
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]

  async function saveSite() {
    const { error } = await supabase
      .from('locations')
      .update({ website: site.trim() || null, updated_at: new Date().toISOString() })
      .eq('id', loc.id)
    if (error) toast({ status: 'error', title: 'Did not save', description: error.message })
    else onChanged()
  }

  async function runReport() {
    if (!site.trim()) {
      toast({ status: 'warning', title: 'Add this location\u2019s page address first' })
      return
    }
    setRunning(true)
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      setRunning(false)
      toast({ status: 'error', title: 'Your session expired. Sign in again.' })
      return
    }
    try {
      const res = await fetch('/.netlify/functions/storefront-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ url: site.trim(), location_id: loc.id, known_name: loc.name }),
      })
      const body = await res.json()
      if (!res.ok || body.ok === false) {
        toast({ status: 'error', title: 'Could not run the report', description: body.error })
      } else {
        toast({ status: 'success', title: `${loc.name} scored ${body.report.overall} out of 100` })
        onChanged()
      }
    } catch {
      toast({ status: 'error', title: 'Could not reach the report function' })
    }
    setRunning(false)
  }

  return (
    <Box bg="surface" borderRadius="md" px={4} py={3}>
      <Flex align="center" gap={3} wrap="wrap">
        <Text fontSize="body-sm" fontWeight={600} color="ink" minW="120px">
          {loc.name}
        </Text>
        <Input
          flex="1 1 220px"
          size="xs"
          value={site}
          onChange={(e) => setSite(e.target.value)}
          onBlur={saveSite}
          placeholder="this location's page address"
          bg="card"
          borderColor="line"
          _focusVisible={{ borderColor: 'accent.500', boxShadow: 'none' }}
        />
        {latest && (
          <HStack spacing={2}>
            <Text fontSize="lg" fontWeight={600} color="ink" fontVariantNumeric="tabular-nums">
              {latest.overall}
            </Text>
            <Link href={`/report/${latest.share_token}/`} isExternal fontSize="11px" color="accent.500" fontWeight={600}>
              Open
            </Link>
          </HStack>
        )}
        <Button
          size="xs"
          variant="outline"
          borderColor="line"
          color="ink"
          leftIcon={<Gauge size={12} />}
          onClick={runReport}
          isLoading={running}
          loadingText="Auditing"
        >
          {latest ? 'Re-run' : 'Run report'}
        </Button>
      </Flex>
    </Box>
  )
}

// ------------------------------------------------------------------------ page

export default function AdminDealerships() {
  const toast = useToast()
  const [state, setState] = useState({ loading: true, rows: [], error: null })
  const [newName, setNewName] = useState('')

  const load = useCallback(async () => {
    const { data, error } = await supabase.from('dealerships').select('*').order('name')
    if (error) {
      setState({ loading: false, rows: [], error: 'Could not load the stores.' })
      return
    }
    setState({ loading: false, rows: data || [], error: null })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  function handleSaved(updated) {
    setState((s) => ({ ...s, rows: s.rows.map((r) => (r.id === updated.id ? updated : r)) }))
  }

  async function addByHand() {
    const name = newName.trim()
    if (!name) return
    const { error } = await supabase.from('dealerships').insert({ name, slug: slugify(name) })
    if (error) {
      toast({ status: 'error', title: 'Could not add', description: error.message })
      return
    }
    setNewName('')
    load()
  }

  return (
    <VStack align="stretch" spacing={6} maxW="sheet">
      <Box>
        <Text textStyle="pageEyebrow" mb={1}>
          Operator
        </Text>
        <Text textStyle="pageTitle">Dealerships</Text>
        <Text fontSize="body" color="inkMuted" lineHeight={1.65} maxW="measure" mt={3}>
          Look a store up, set its brand, then invite its people. A dealership in
          Arkansas should not open the same product as one in Oregon.
        </Text>
      </Box>

      {/* The whole job in one flow. The lookup card below still exists for
          adding a store to an existing list without the ceremony. */}
      <Box
        as={RouterLink}
        to="/admin/onboard/"
        display="block"
        bg="ink"
        color="onInk"
        borderRadius="card"
        p={{ base: 5, md: 6 }}
        _hover={{ textDecoration: 'none', transform: 'translateY(-1px)' }}
        transition="transform 0.15s ease"
      >
        <HStack spacing={3} mb={2}>
          <Icon as={Building2} boxSize={4} color="onInkMuted" />
          <Text textStyle="eyebrow" color="onInkMuted">
            New dealership
          </Text>
        </HStack>
        <Text fontSize="display-sm" fontWeight={600} letterSpacing="-0.02em" mb={2}>
          Bring a store on in one pass
        </Text>
        <Text fontSize="body-sm" color="onInkMuted" lineHeight={1.6} maxW="measure">
          Look them up, confirm who they are, add their buildings and invite the owner, the
          manager and the floor. Nothing is emailed until the last step.
        </Text>
      </Box>

      <Lookup onCreated={load} />

      {state.loading && (
        <Flex justify="center" py={10}>
          <Spinner size="md" color="ink" thickness="3px" />
        </Flex>
      )}

      {state.error && (
        <Alert status="error" borderRadius="md" fontSize="sm">
          <AlertIcon />
          {state.error}
        </Alert>
      )}

      {!state.loading && !state.error && (
        <>
          <HStack spacing={2}>
            <Icon as={Building2} boxSize={4} color="inkMuted" />
            <Text textStyle="eyebrow" color="inkMuted">
              {state.rows.length} {state.rows.length === 1 ? 'store' : 'stores'}
            </Text>
          </HStack>

          {state.rows.map((store) => (
            <StoreCard key={store.id} store={store} onSaved={handleSaved} />
          ))}

          <Box bg="surface" borderRadius="card" p={5}>
            <HStack spacing={2} mb={2}>
              <Icon as={Globe} boxSize={3.5} color="inkMuted" />
              <Text textStyle="eyebrow" color="inkMuted">
                Add one by hand
              </Text>
            </HStack>
            <Text fontSize="12px" color="inkMuted" mb={3} maxW="measure">
              For a store with no usable website. The brand fields can be filled in
              afterward.
            </Text>
            <Flex gap={3} wrap="wrap">
              <Input
                flex="1 1 240px"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addByHand()}
                placeholder="Store name"
                bg="card"
                borderColor="line"
                size="sm"
                fontSize="body-sm"
                _focusVisible={{ borderColor: 'accent.500', boxShadow: 'none' }}
              />
              <Button size="sm" variant="outline" borderColor="line" color="ink" onClick={addByHand}>
                Add store
              </Button>
            </Flex>
          </Box>
        </>
      )}
    </VStack>
  )
}
